<script setup>
import { ref, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import ScrollTop from 'primevue/scrolltop'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import AppDialog from '@/components/ui/AppDialog.vue'
import { useUnsavedChangesStore } from '@/stores/unsavedChanges'
import { useI18n } from '@/i18n'
import { isNative, platform } from '@/services/platform'
import { tokenStore } from '@/services/tokenStore'
import { biometricStore } from '@/services/biometricStore'
import { useAlarmNotificationsStore } from '@/stores/alarmNotifications'
import { useNotificationsStore } from '@/stores/notifications'

const router    = useRouter()
const toast     = useToast()
const unsavedChanges = useUnsavedChangesStore()
const { t }     = useI18n()
const alarmNotifications = useAlarmNotificationsStore()
const notifications      = useNotificationsStore()

// ── Boot-Initialisierung ──────────────────────────────────────────────────────
// false bis tokenStore.init() abgeschlossen ist.
// main.js macht tokenStore.init() VOR dem Mount — dadurch fast sofort true nach Mount.
// (Bug-Fix: getSync() liefert null vor init, und null !== undefined war immer true —
//  deshalb war das Overlay nie sichtbar. Jetzt korrekt mit ref(false) initialisiert.)
const isInitialized = ref(false)

// ── Biometric Lock ────────────────────────────────────────────────────────────
// true = Biometrie-Overlay ist sichtbar (App gesperrt)
const biometricLocked    = ref(false)
const biometricLabel     = ref('Face ID')  // 'Face ID' | 'Touch ID' | 'Biometrie'
const biometricAuthBusy  = ref(false)

/**
 * Zeigt das Biometrie-Overlay und löst sofort die Authentifizierung aus.
 * Wird aufgerufen beim Start (wenn Token + Biometrie aktiviert) sowie
 * beim App-Resume nach längerem Hintergrund-Aufenthalt.
 */
async function lockAndAuthenticate() {
  if (!isNative) return
  biometricLocked.value = true
  biometricLabel.value  = await biometricStore.getBiometryLabel()
  await runBiometricAuth()
}

async function runBiometricAuth() {
  if (biometricAuthBusy.value) return
  biometricAuthBusy.value = true

  const reason = t('biometric.unlockReason')
  const ok = await biometricStore.authenticate(reason)

  biometricAuthBusy.value = false

  if (ok) {
    biometricLocked.value = false
  }
  // Bei Fehlschlag: Overlay bleibt offen, User sieht "Mit Passwort öffnen"-Button
}

/** "Passwort statt Biometrie" — Token löschen, zur Login-Seite */
async function usePasswordInstead() {
  biometricLocked.value = false
  await tokenStore.clear()
  router.push('/login')
}

// ── Push-Notification-Service (Phase 5) ─────────────────────────────────────
// Registrierung läuft erst nachdem der User eingeloggt ist (JWT vorhanden).
// pushService ist ein separates Modul — lazy importiert, damit Web-Build
// den @capacitor/push-notifications Import nicht verarbeiten muss wenn
// isNative === false. (Tree-shaking greift in der Praxis nicht in Vue CLI,
// deshalb initialisieren wir es nur wenn nötig.)
async function initPushNotifications() {
  if (!isNative) return
  if (!tokenStore.getSync()) return

  try {
    const { pushService } = await import('@/services/pushService')
    await pushService.init()
  } catch (err) {
    console.warn('[App] Push-Notification-Init fehlgeschlagen:', err)
  }
}

// ── Browser-Alarm-Benachrichtigungen (Web, Partner-User) ────────────────────
// Selbst-gated: läuft nur im Browser (nicht native) + nur für Partner-User mit
// erteilter Notification-Berechtigung. Der Poll-Tick prüft Login/Partner erneut.
function initAlarmNotifications() {
  if (isNative) return
  alarmNotifications.start(router, t)
}

// ── Unsaved Changes ───────────────────────────────────────────────────────────
const confirmLeave = () => {
  const target = unsavedChanges.confirmNavigation()
  if (target) router.push(target)
}

const stayOnPage = () => {
  unsavedChanges.cancelNavigation()
}

const handleBeforeUnload = (event) => {
  if (!unsavedChanges.isDirty) return
  event.preventDefault()
  event.returnValue = ''
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────
// ── Push-Notification In-App-Toast ────────────────────────────────────────────
function handlePushNotificationEvent(event) {
  const { title, body } = event.detail || {}
  toast.add({
    severity: 'info',
    summary: title || t('common.hint'),
    detail: body || '',
    life: 6000,
    group: 'global',
  })
}

onMounted(async () => {
  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('secsu:push-notification', handlePushNotificationEvent)

  // Plattform-Klassen auf <body> setzen.
  // Scopen alle platform-ios.css-Selektoren — im Browser kein Effekt.
  if (isNative) {
    document.body.classList.add('platform-native')
    document.body.classList.add(`platform-${platform}`) // platform-ios | platform-android
  }

  // Sicherheitsnetz: falls tokenStore.init() noch nicht fertig war
  await tokenStore.init()
  isInitialized.value = true

  if (isNative) {
    // Biometrie-Cache füllen (parallel, blockiert nicht den Mount)
    await biometricStore.init()

    // Sofort sperren wenn JWT vorhanden + Biometrie aktiviert
    const hasToken = Boolean(tokenStore.getSync())
    if (hasToken && biometricStore.isEnabledSync()) {
      await lockAndAuthenticate()
    }

    // App-Lifecycle: Capacitor @capacitor/app für resume/pause Events
    setupAppLifecycle()
  }

  // Push-Notifications initialisieren (nach Biometrie-Unlock läuft dies ggf.
  // erst nach erfolgter Entsperrung — watch auf biometricLocked unten)
  if (!biometricLocked.value) {
    await initPushNotifications()
  }

  // Browser-Alarm-Benachrichtigungen (Web-only, self-gated)
  initAlarmNotifications()

  // Generisches In-App-Postfach + Browser-Benachrichtigungen (alle Alarmtypen).
  // Läuft auch native (Inbox/Badge); Web-Notification-Firing ist im Store self-gated.
  notifications.start(router, t)
})

// Push-Init auslösen sobald Biometrie-Overlay verschwindet
watch(biometricLocked, async (locked) => {
  if (!locked) {
    await initPushNotifications()
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('secsu:push-notification', handlePushNotificationEvent)
  alarmNotifications.stop()
  notifications.stop()
  _appListenerHandle?.remove().catch(() => {})
})

// ── App-Lifecycle (Capacitor) ─────────────────────────────────────────────────
let _appListenerHandle = null

async function setupAppLifecycle() {
  try {
    const { App: CapApp } = await import('@capacitor/app')

    _appListenerHandle = await CapApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        // App geht in Hintergrund
        biometricStore.markBackground()
      } else {
        // App kehrt in Vordergrund zurück
        if (biometricStore.shouldLockOnResume() && tokenStore.getSync()) {
          lockAndAuthenticate()
        }
      }
    })
  } catch (err) {
    console.warn('[App] App-Lifecycle-Listener fehlgeschlagen:', err)
  }
}
</script>

<template>
  <!-- Global PrimeVue services — immer im DOM, nie hinter v-if.
       Toast nutzt Teleport intern; ConfirmDialog ebenso.
       Würden diese hinter v-if verschwinden, reißt der Teleport-Mount. -->
  <Toast position="top-right" group="global" />
  <ConfirmDialog />
  <AppDialog
    v-model="unsavedChanges.showDialog"
    :title="t('dialogs.unsavedTitle')"
    :show-footer="false"
    :closable="false"
    :disable-outside-close="true"
    width="460px"
  >
    <p>{{ t('dialogs.unsavedBody') }}</p>
    <div class="flex justify-content-end gap-2 mt-4">
      <pButton :label="t('dialogs.reviewChanges')" severity="secondary" @click="stayOnPage" />
      <pButton :label="t('dialogs.discardLeave')" severity="danger" @click="confirmLeave" />
    </div>
  </AppDialog>

  <router-view />
  <ScrollTop />

  <!-- ── Boot-Overlay ──────────────────────────────────────────────────── -->
  <!-- Erscheint kurz beim Start solange tokenStore.init() läuft.
       Position:fixed → überlagert alles, blockiert keine Mounts darunter. -->
  <Transition name="boot-fade">
    <div v-if="!isInitialized" class="app-boot-overlay">
      <i class="pi pi-spin pi-spinner app-boot-spinner" />
      <p class="app-boot-label">{{ t('app.initializing') }}</p>
    </div>
  </Transition>

  <!-- ── Biometric Lock Overlay (Phase 4) ─────────────────────────────── -->
  <!-- Erscheint nach App-Start oder Resume wenn Biometrie aktiviert ist.
       Liegt über allem (z-index: 10000), verhindert Zugriff auf App-Inhalte. -->
  <Transition name="bio-fade">
    <div v-if="biometricLocked" class="bio-lock-overlay">
      <div class="bio-lock-card">
        <!-- App-Logo / Icon -->
        <div class="bio-lock-icon">
          <i class="pi pi-shield" />
        </div>

        <p class="bio-lock-app">secDASH</p>
        <p class="bio-lock-hint">{{ t('biometric.lockedHint') }}</p>

        <!-- Biometrie-Button -->
        <pButton
          :icon="biometricLabel === 'Face ID' ? 'pi pi-eye' : 'pi pi-fingerprint'"
          :label="biometricLabel"
          :loading="biometricAuthBusy"
          class="bio-lock-btn"
          rounded
          @click="runBiometricAuth"
        />

        <!-- Passwort-Fallback -->
        <button class="bio-lock-password-btn" @click="usePasswordInstead">
          {{ t('biometric.usePassword') }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* ── Boot-Overlay ────────────────────────────────────────────────────────── */
.app-boot-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background: var(--p-surface-ground, #111);
  z-index: 9999;
}
.app-boot-spinner {
  font-size: 2rem;
  color: var(--p-primary-color, #e60050);
}
.app-boot-label {
  font-size: 0.875rem;
  color: var(--p-text-color-secondary, #aaa);
  margin: 0;
}

/* Sanftes Ausblenden sobald isInitialized = true */
.boot-fade-leave-active { transition: opacity 0.2s ease; }
.boot-fade-leave-to     { opacity: 0; }

/* ── Biometric Lock Overlay ──────────────────────────────────────────────── */
.bio-lock-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--p-surface-ground, #0f172a);
  z-index: 10000;
  /* Safe-Area: Notch + Home-Indicator */
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.bio-lock-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 32px;
  max-width: 320px;
  width: 100%;
  text-align: center;
}

.bio-lock-icon {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  background: var(--p-primary-100, #dbeafe);
  color: var(--p-primary-color, #3b82f6);
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.bio-lock-app {
  font-size: 1.3rem;
  font-weight: 800;
  color: var(--p-text-color);
  margin: 0;
  letter-spacing: -0.01em;
}

.bio-lock-hint {
  font-size: 0.85rem;
  color: var(--p-text-color-secondary);
  margin: 0 0 8px;
  line-height: 1.5;
}

.bio-lock-btn {
  width: 100%;
  justify-content: center;
  font-size: 1rem;
  padding: 12px 24px;
}

.bio-lock-password-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.82rem;
  color: var(--p-text-color-secondary);
  padding: 8px 0;
  text-decoration: underline;
  text-underline-offset: 3px;
  opacity: 0.7;
  transition: opacity 0.12s;
}
.bio-lock-password-btn:hover {
  opacity: 1;
}

/* Einblenden/Ausblenden */
.bio-fade-enter-active, .bio-fade-leave-active {
  transition: opacity 0.25s ease;
}
.bio-fade-enter-from, .bio-fade-leave-to {
  opacity: 0;
}

/* ── Dark Mode ────────────────────────────────────────────────────────────── */
:global(.my-app-dark) .bio-lock-overlay {
  background: var(--p-surface-900, #0f172a);
}
:global(.my-app-dark) .bio-lock-icon {
  background: var(--p-primary-900, #1e3a5f);
  color: var(--p-primary-300, #93c5fd);
}
</style>
